import { IonContent, IonPage } from "@ionic/react";

import { Hero, Over } from "../components/Hero";
import { I } from "../components/icons";
import { Notice } from "../components/Notice";
import { NavRow, RowGroup } from "../components/Rows";
import { Screen } from "../components/Shell";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { dayLabel, parseDay, todayStr } from "../domain/date";
import { dayKcal, stepBreakdown } from "../domain/energy";
import { workLine } from "../domain/foodView";
import { WORK_CREDIT, deficitTarget, energyBalance, entriesFor, foodOn } from "../domain/intake";
import { dayLedger } from "../domain/ledger";
import { workParts } from "../domain/today";
import { useDeviceAnswers, useKaya } from "../store/kayaStore";
import "./Food.css";

const n = (v: number) => v.toLocaleString();
const est = (v: number | null) => (v === null ? "–" : `~${n(v)}`);

/**
 * Energy today (`/food/energy`): where today's Out comes from, and what that leaves.
 *
 * Today only — the same ledger over 14 days is `/progress/energy`. The first three rows are the
 * burn: two formulas (so "~") and the work Kaya measured, which is added on top rather than
 * multiplied in, and which has its own page under Today. Then In, the Budget that Out minus the
 * planned deficit and the uncredited part of Work give (ADR-041), and what is Left.
 */
export default function FoodEnergy() {
  const S = useKaya((s) => s.S);
  const device = useDeviceAnswers();
  const ds = todayStr();
  const measured = Math.round(dayKcal(S, ds));
  const bal = energyBalance(S, ds, measured, parseDay(ds), device);
  const led = dayLedger(S, ds, undefined, device);
  const work = workParts(S, ds);
  const steps = stepBreakdown(S, ds).total;
  const on = foodOn(S);
  const count = entriesFor(S, ds).length;
  const deficit = deficitTarget(S, undefined, device);
  const left = bal ? bal.left : null;

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <div className="k-food">
            <TopBar title="Energy today" back={{ to: "/food", label: "Back to Food" }} />
            <Hero
              label="Out today, est."
              figure={bal ? `~${n(bal.out)}` : null}
              unit="kcal"
              line={
                bal
                  ? `${dayLabel(ds)} · rises as work is logged`
                  : "Add your year of birth and sex to see the day’s burn."
              }
            />

            {!bal ? (
              <RowGroup>
                <NavRow
                  icon={I.user}
                  title="Add your body numbers"
                  subtitle="Resting burn is most of the day, and needs them"
                  to="/you/body"
                />
              </RowGroup>
            ) : null}

            <RowGroup heading="Out, est. · kcal">
              <NavRow kind="value" title="At rest" subtitle="From weight, height and age" value={est(bal?.rest ?? null)} />
              <NavRow
                kind="value"
                title="Everyday"
                subtitle={steps > 0 ? `${n(steps)} steps and moving about` : "Moving about, beyond the work"}
                value={est(bal?.baseline ?? null)}
              />
              <NavRow
                icon={I.flame}
                title="Work"
                subtitle={workLine(work) ?? "No session, treadmill or walk yet"}
                value={est(work.total)}
                to="/today/activity"
              />
              <NavRow kind="value" title="Out" value={est(bal?.out ?? null)} />
            </RowGroup>

            <RowGroup heading="In and budget · kcal">
              {on ? (
                <NavRow
                  kind="value"
                  title="In"
                  subtitle={count ? `${count} ${count === 1 ? "entry" : "entries"} logged` : "Nothing logged yet"}
                  value={
                    led.in !== null ? (
                      <>
                        <Tag tone="logged" /> {n(led.in)}
                      </>
                    ) : (
                      "–"
                    )
                  }
                />
              ) : (
                <NavRow title="In" subtitle="Food tracking is off" value="–" to="/you/food" />
              )}
              <NavRow
                kind="value"
                title="Budget"
                subtitle={
                  bal && bal.heldBack
                    ? `= out − ${n(deficit)} planned deficit − ${n(bal.heldBack)} of work`
                    : `= out − ${n(deficit)} planned deficit`
                }
                value={est(bal?.budget ?? null)}
              />
              <NavRow
                kind="value"
                title="Left"
                value={
                  left === null ? "–" : left < 0 ? <Over>{`${n(-left)} over`}</Over> : est(left)
                }
              />
            </RowGroup>

            {/* Out and Work stay whole here; only the budget discounts work (ADR-041). */}
            <Notice tone="info">
              Work is added to your resting and everyday burn, not multiplied into it, so a session
              is never counted twice. The budget counts it at {WORK_CREDIT}: people eat back part of
              what they burn and the estimates run high.
            </Notice>

            <RowGroup>
              <NavRow
                icon={I.edit}
                title="Change the deficit"
                subtitle="You › Food & calories"
                value={`${n(deficit)} kcal`}
                to="/you/food"
              />
              <NavRow
                icon={I.progress}
                title="Over 14 days"
                subtitle="In against out, day by day"
                to="/progress/energy"
              />
            </RowGroup>
          </div>
        </Screen>
      </IonContent>
    </IonPage>
  );
}
